import { LoadingOutlined } from "@ant-design/icons";
import { Button as AntButton } from "antd";
import { ButtonProps } from "../types/item";
import { twMerge } from "tailwind-merge";
import { useState } from "react";
import useTheme from "../hooks/theme";
import Text from "./text";

const actionFill: Record<string, string> = {
  primary: "!bg-action-primary !border-action-primary",
  base: "!bg-action-base !border-action-base",
  secondary: "!bg-action-secondary !border-action-secondary",
  success: "!bg-action-success !border-action-success",
};

const fillText: Record<string, string> = {
  primary: "!text-on-accent",
  base: "!text-on-primary",
  secondary: "!text-on-secondary",
  success: "!text-on-primary",
};

const actionOutline: Record<string, string> = {
  primary: "!bg-transparent !border-action-primary !text-accent",
  base: "!bg-transparent !border-action-base !text-primary",
  secondary: "!bg-transparent !border-action-secondary !text-secondary",
  success: "!bg-transparent !border-action-success !text-action-success",
};

const Button = ({
  title = "",
  type = "primary",
  color = "primary",
  onClick,
  outlined,
  minWidth,
  icon,
  disabled,
  shape,
  className = "",
  style,
  ...props
}: ButtonProps) => {
  const theme = useTheme();
  const [internalLoading, setInternalLoading] = useState(false);
  const iconOnly = !!icon && !title;
  const loading = internalLoading || props.loading;
  const labelColor = outlined
    ? {
        primary: theme.colors.accent,
        base: theme.colors.primary,
        secondary: theme.colors.secondary,
        success: theme.colors.font.success,
      }[color] || theme.colors.onAccent
    : iconOnly
      ? theme.colors.onPrimary
      : {
          primary: theme.colors.onAccent,
          base: theme.colors.onPrimary,
          secondary: theme.colors.onSecondary,
          success: theme.colors.onPrimary,
        }[color] || theme.colors.onAccent;

  const onClickButton = async (e: any) => {
    if (!onClick) return;
    e.stopPropagation();
    const result = onClick(e) as void | Promise<unknown>;
    if (!(result instanceof Promise)) return;
    setInternalLoading(true);
    try {
      await result;
    } catch (error) {
    } finally {
      setInternalLoading(false);
    }
  };

  if (type === "text")
    return (
      <Text
        level={5}
        type={color}
        onClick={onClickButton}
        className="cursor-pointer transition-transform duration-150 ease-out active:scale-98"
      >
        {loading ? (
          <LoadingOutlined />
        ) : (
          <>
            {icon}
            {title}
          </>
        )}
      </Text>
    );

  const fill = actionFill[color] || actionFill.primary;
  const tone = outlined
    ? actionOutline[color] || actionOutline.primary
    : `${fill} ${iconOnly ? "!text-on-primary" : fillText[color] || fillText.primary}`;

  const minWClass = minWidth ? "" : iconOnly ? "min-w-[4rem]" : "min-w-[12rem]";
  const pill =
    shape === "round" || shape === "circle" || style?.borderRadius != null;

  return (
    <AntButton
      {...props}
      type={type}
      icon={icon}
      disabled={disabled}
      loading={loading}
      onClick={onClickButton}
      shape={shape}
      className={twMerge(
        "h-[4rem]",
        !pill && "!rounded-card",
        minWClass,
        tone,
        disabled && "!opacity-50",
        className,
      )}
      style={{ color: labelColor, ...(minWidth ? { minWidth } : {}), ...style }}
    >
      {!loading && !!title && (
        <Text color={labelColor} children={title} weight={600} />
      )}
    </AntButton>
  );
};

export default Button;
